import React, { ReactElement, useContext, useState } from 'react'
import { ArtData } from '@/database/art'
import { shortenAddress } from '@/utils/shortenAddress'
import { Context } from '@/components/Context'
import Button from '@/components/Button'
import Box from '@/components/boxes/Box'
import { formatDateString, priceText, transferFeeText } from '@/utils/formatting'
import { ArtState } from '@/database/types'
import LoadImageOnDemand from '@/components/LoadImageOnDemand'
import CheckBox from '@/components/boxes/CheckBox'

const MyArtWork = ({ isOwned, artData }: { isOwned: boolean; artData: ArtData }): ReactElement => {
    const { accountSignature, proposeHandler, currentAccount, toggleHideArt, transferNft } = useContext(Context)
    const [transferToggled, setTransferToggled] = useState(false)
    const [recipient, setRecipient] = useState('')
    const [transferFee, setTransferFee] = useState('')

    const isProposable = (artData: ArtData): boolean => {
        return !artData.image?.url && artData.art.user.walletAddress === currentAccount
    }

    const isHidden = (artData: ArtData): boolean => {
        return artData.art.state === ArtState.HIDDEN
    }

    const handleReceiverInputChange = (event: { target: { value: string } }) => {
        setRecipient(event.target.value)
    }

    const transactionHandler = async (event: { preventDefault: () => void }) => {
        event.preventDefault()
        if (!recipient) {
            window.alert('Please fill all the fields')
            return
        }
        if (!accountSignature) {
            window.alert('Please connect your wallet to proceed transaction')
            return
        }
        await transferNft(artData, recipient, transferFee)
    }

    const handleTransferFeeInputChange = (event: { target: { value: string } }) => {
        const input = event.target.value.replace(',', '.') // Normalize input

        // Check if the last character is a dot or if the input is just a dot
        const endsWithDot = input.endsWith('.') || input === '.'

        // Use a regular expression to validate if the input is in a correct numeric format (including dots)
        const isValidNumericInput = /^(\d+\.?\d*|\.\d+)$/.test(input)

        if (endsWithDot || isValidNumericInput) {
            // Directly update the state with the input if it's a valid numeric input or ends with a dot
            const newInput = parseFloat(input)
            setTransferFee(newInput.toString())
        } else if (input === '') {
            setTransferFee(artData?.art.transferFee.toString())
        }
    }

    return (
        <div className="header-container width400 center">
            <div key={artData.art.id} className="artwork-box">
                <div className={'header-container'}>
                    <Box caption={'Title'} text={artData.art.title} />
                </div>
                {isProposable(artData) && (
                    <div className={'header-container bottom'}>
                        <Box caption={'Creator'} text={shortenAddress(artData.art.user.walletAddress)} />
                        <Box caption={'Time'} text={formatDateString(artData.art.createdAt)} />
                    </div>
                )}
                {!isProposable(artData) && (
                    <div className="header-container bottom">
                        <Box caption={'Owner'} text={shortenAddress(artData.art.currentOwner)} />
                    </div>
                )}
                <div className="portfolio-image-container">
                    <LoadImageOnDemand
                        id={'art' + artData.art.id}
                        alt={artData.art.prompt}
                        imageId={artData.image.id}
                        style="z-index-zero"
                        link={'/crazy#nft-' + artData.tokenId + '/'}
                        objectFit="cover"
                        useFill={true}
                    />
                </div>
                <div className={'header-container'}>
                    <Box caption={'Description'} text={artData.art.prompt} styleText={'small'} />
                </div>
                <div className={'header-container'}>
                    <Box
                        caption={'Sale Price'}
                        text={priceText(artData)}
                        textValue={artData?.art.price}
                        triggerAnimation={false}
                    />
                    <Box
                        caption={'Transfer Fee'}
                        text={transferFeeText(artData)}
                        textValue={artData?.art.transferFee}
                        triggerAnimation={false}
                    />
                </div>
                <h4>
                    {isProposable(artData) && (
                        <Button
                            btnName="Propose&nbsp;NFT&nbsp;Mint"
                            classStyles="nav__medium long-button red"
                            handleClick={() => proposeHandler(artData)}
                            cancelBg={undefined}
                        />
                    )}
                    {isOwned && (
                        <React.Fragment>
                            <CheckBox
                                btnName={'Transfer'}
                                classStyles="nav__medium blue  "
                                handleClick={() => setTransferToggled(!transferToggled)}
                                checked={transferToggled}
                            />
                            {transferToggled && (
                                <div className={''}>
                                    <div>
                                        <input
                                            className="width335"
                                            placeholder="... Receiver Address ..."
                                            value={recipient}
                                            onChange={handleReceiverInputChange}
                                        />
                                    </div>
                                    <div>
                                        <input
                                            className="width250"
                                            type="number"
                                            min={artData?.art.transferFee}
                                            placeholder="... Transfer Fee In CRAZY ..."
                                            value={transferFee}
                                            onChange={handleTransferFeeInputChange}
                                        />
                                    </div>
                                    <Button
                                        btnName={`Send`}
                                        classStyles={'nav__medium pink center bottom'}
                                        disabled={!accountSignature || !recipient}
                                        handleClick={(event: { preventDefault: () => void }) =>
                                            accountSignature
                                                ? transactionHandler(event)
                                                : alert('Please connect your wallet to proceed transaction')
                                        }
                                        cancelBg={undefined}
                                    />
                                </div>
                            )}
                            <div>
                                <Button
                                    btnName={isHidden(artData) ? 'Unhide' : 'Hide'}
                                    classStyles="nav__medium blue"
                                    handleClick={() => toggleHideArt(artData)}
                                    cancelBg={undefined}
                                />

                                <Button
                                    btnName={'Burn'}
                                    classStyles="nav__medium red"
                                    disabled={true}
                                    handleClick={() => toggleHideArt(artData)}
                                    cancelBg={undefined}
                                />
                            </div>
                        </React.Fragment>
                    )}
                </h4>
            </div>
        </div>
    )
}

export default MyArtWork
